/**
 * V70 Phase B 긴급 수정 (2026-10-20) — 관리자/참가자 양식 상태 표시 **단일 소스**.
 *
 * publicStatus 뿐만 아니라 신청기간을 함께 계산하여 7가지 effective 상태를 반환.
 * 관리자(TournamentApplyAdminPage.tsx) 와 참가자(TournamentApplyPage.tsx) 가
 * 이 유틸을 **공통으로 사용** → 두 화면의 상태 라벨/색상이 항상 일치.
 */

type RawTimestampLike = { toMillis?: () => number; _seconds?: number; seconds?: number } | null | undefined;

export type FormPublicStatus = 'draft' | 'open' | 'paused' | 'closed' | 'archived';

export type EffectiveFormStatus =
  | 'draft'       // publicStatus='draft'
  | 'scheduled'   // publicStatus='open' + 시작 전
  | 'in-progress' // publicStatus='open' + 기간 내
  | 'ended'       // publicStatus='open' + 종료 후
  | 'paused'      // publicStatus='paused'
  | 'closed'      // publicStatus='closed'
  | 'archived';   // publicStatus='archived'

export function tsToMs(ts: RawTimestampLike): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts._seconds === 'number') return ts._seconds * 1000;
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

export function computeEffectiveFormStatus(
  publicStatus: FormPublicStatus,
  applyStartAt: RawTimestampLike,
  applyEndAt: RawTimestampLike,
  nowMs: number = Date.now(),
): EffectiveFormStatus {
  switch (publicStatus) {
    case 'draft': return 'draft';
    case 'paused': return 'paused';
    case 'closed': return 'closed';
    case 'archived': return 'archived';
    case 'open': {
      const startMs = tsToMs(applyStartAt);
      const endMs = tsToMs(applyEndAt);
      if (startMs > 0 && nowMs < startMs) return 'scheduled';
      if (endMs > 0 && nowMs > endMs) return 'ended';
      return 'in-progress';
    }
  }
}

export function effectiveStatusLabel(s: EffectiveFormStatus): string {
  switch (s) {
    case 'draft':       return '작성중';
    case 'scheduled':   return '접수 예정';
    case 'in-progress': return '접수중';
    case 'ended':       return '접수 종료';
    case 'paused':      return '일시중지';
    case 'closed':      return '종료';
    case 'archived':    return '보관';
  }
}

export function effectiveStatusColor(s: EffectiveFormStatus): string {
  switch (s) {
    case 'draft':       return '#6b7280';
    case 'scheduled':   return '#2563eb';
    case 'in-progress': return '#059669';
    case 'ended':       return '#dc2626';
    case 'paused':      return '#d97706';
    case 'closed':      return '#4b5563';
    case 'archived':    return '#4b5563';
  }
}

/** 실제 신청 가능 여부 — effective='in-progress' 와 1:1 일치 */
export function isAcceptingApplications(
  publicStatus: FormPublicStatus,
  applyStartAt: RawTimestampLike,
  applyEndAt: RawTimestampLike,
  nowMs: number = Date.now(),
): boolean {
  return computeEffectiveFormStatus(publicStatus, applyStartAt, applyEndAt, nowMs) === 'in-progress';
}

/** 한국시간(KST) 형식 — YYYY-MM-DD HH:MM */
export function fmtKstDateTime(ms: number): string {
  if (!ms || ms <= 0) return '-';
  const d = new Date(ms);
  // Asia/Seoul = UTC+9
  const kst = new Date(d.getTime() + 9 * 3600 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${kst.getUTCFullYear()}-${pad(kst.getUTCMonth() + 1)}-${pad(kst.getUTCDate())} ${pad(kst.getUTCHours())}:${pad(kst.getUTCMinutes())} KST`;
}
